<template>
  <div>
    <h1 class="flex mb-8 font-bold text-3xl">
      <icon name="passport" class="w-6 h-9 mr-3 fill-gray-700" /> Indicadores
    </h1>

    <div class="mb-6 flex justify-between items-center">
      <text-input v-model="form.search" class="w-full max-w-md mr-4" placeholder="Buscar..." @reset="reset" />

      <inertia-link class="btn-indigo" :href="route('indicators.create')">
        <span>Criar</span>
        <span class="hidden md:inline">Indicador</span>
      </inertia-link>
    </div>

    <div class="bg-white rounded-md shadow overflow-x-auto">
      <table class="w-full whitespace-nowrap">
        <tr class="text-left font-bold">
          <th class="px-6 pt-6 pb-4">Nome</th>
          <th class="px-6 pt-6 pb-4">Tipo</th>
          <th v-if="show" class="px-6 pt-6 pb-4">Organização</th>
          <th v-if="show" class="px-6 pt-6 pb-4">Perfil</th>
          <th v-if="show" class="px-6 pt-6 pb-4">Cidade/UF</th>
          <th class="px-6 pt-6 pb-4">Última Alteração</th>
          <th class="px-6 pt-6 pb-4">Vigente</th>
          <th class="px-6 pt-6 pb-4" colspan="2">Valor</th>
        </tr>

        <tr v-for="indicator in indicators.data" :key="indicator.id" class="cursor-pointer hover:bg-gray-100 focus-within:bg-gray-100">
          <td class="border-t">
            <inertia-link v-if="indicator.editable" class="px-6 py-4 flex items-center focus:text-indigo-500" :href="route('indicators.edit', indicator.id)">
              {{ indicator.label }}
            </inertia-link>
            <span v-else class="px-6 py-4 flex items-center focus:text-indigo-500">
              {{ indicator.label }}
            </span>
          </td>
          <td class="border-t">
            <inertia-link v-if="indicator.editable" class="px-6 py-4 flex items-center focus:text-indigo-500" :href="route('indicators.edit', indicator.id)">
              {{ indicator.type }}
            </inertia-link>
            <span v-else class="px-6 py-4 flex items-center focus:text-indigo-500">
              {{ indicator.type }}
            </span>
          </td>

          <td class="border-t">
            <inertia-link v-if="indicator.editable" class="px-6 py-4 flex items-center focus:text-indigo-500" :href="route('indicators.edit', indicator.id)">
              {{ indicator.organization }}
            </inertia-link>
            <span v-else class="px-6 py-4 flex items-center focus:text-indigo-500">
              {{ indicator.organization }}
            </span>
          </td>

          <td class="border-t">
            <inertia-link v-if="indicator.editable" class="px-6 py-4 flex items-center focus:text-indigo-500" :href="route('indicators.edit', indicator.id)">
              {{ indicator.role }}
            </inertia-link>
            <span v-else class="px-6 py-4 flex items-center focus:text-indigo-500">
              {{ indicator.role }}
            </span>
          </td>

          <td class="border-t">
            <inertia-link v-if="indicator.editable" class="px-6 py-4 flex items-center focus:text-indigo-500" :href="route('indicators.edit', indicator.id)">
              {{ getCityStateName(indicator) }}
            </inertia-link>
            <span v-else class="px-6 py-4 flex items-center focus:text-indigo-500">
              {{ getCityStateName(indicator) }}
            </span>
          </td>

          <td class="border-t">
            <inertia-link v-if="indicator.editable" class="px-6 py-4 flex items-center focus:text-indigo-500" :href="route('indicators.edit', indicator.id)">
              {{ indicator.updated_at }}
            </inertia-link>
            <span v-else class="px-6 py-4 flex items-center focus:text-indigo-500">
              {{ indicator.updated_at }}
            </span>
          </td>

          <td class="border-t">
            <inertia-link v-if="indicator.editable" class="px-6 py-4 flex items-center focus:text-indigo-500" :href="route('indicators.edit', indicator.id)">
              <active-badge :active="indicator.active" />
            </inertia-link>
            <span v-else class="px-6 py-4 flex items-center focus:text-indigo-500">
              <active-badge :active="indicator.active" />
            </span>
          </td>

          <td class="border-t w-px">
            <inertia-link v-if="indicator.editable" class="px-6 py-4 flex items-center focus:text-indigo-500" :href="route('indicators.edit', indicator.id)" tabindex="-1">
              {{ indicator.value }}
              <icon name="cheveron-right" class="block w-6 h-6 ml-auto mr-0 fill-gray-400" />
            </inertia-link>
            <span v-else class="px-6 py-4 flex items-center focus:text-indigo-500">
              {{ indicator.value }}
            </span>
          </td>
        </tr>

        <tr v-if="indicators.data.length === 0">
          <td class="border-t px-6 py-6" :colspan="show ? 8 : 6">Nenhum registro</td>
        </tr>
      </table>
    </div>

    <pagination class="mt-6" :links="indicators.links" />
  </div>
</template>

<script>
import Icon from '@/Shared/Icon'
import pickBy from 'lodash/pickBy'
import Layout from '@/Shared/Layout'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination'
import TextInput from '../../Shared/TextInput'
import ActiveBadge from '@/Shared/ActiveBadge'

export default {
  metaInfo: { title: 'Indicadores' },
  components: {
    Icon,
    TextInput,
    Pagination,
    ActiveBadge,
  },
  layout: Layout,
  props: {
    indicators: Object,
    filter: Object,
    show: Boolean,
  },
  data() {
    return {
      form: {
        search: this.filter.search,
      },
    }
  },
  watch: {
    form: {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get(this.route('indicators.register'), pickBy(this.form), { preserveState: true })
      }, 150),
    },
  },
  methods: {
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    getCityStateName(indicator) {
      if(indicator.city && indicator.state)
      {
        return `${indicator.city}/${indicator.state}`
      }

      return '-'
    },
  },
}
</script>
